import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { getApi } from '../../../Services/apiService';
import { API_BASE_URL } from '../../../Middleware/interceptor';
import CustomDropdown from '../../Common/CustomDropdown';

// API origin without trailing slash, for the raw fetch calls below.
const API_ORIGIN = API_BASE_URL.replace(/\/+$/, '');

const Dashboard = () => {
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editingShelterId, setEditingShelterId] = useState(null);
    const [deletingShelterId, setDeletingShelterId] = useState(null);
    const [viewingShelterId, setViewingShelterId] = useState(null);
    const [viewShelterData, setViewShelterData] = useState(null);
    const [viewLoader, setViewLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        cityRegion: '',
        posterLanguage: '',
        logo: null,
        isActive: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    
    // API state
    const [shelters, setShelters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [search, setSearch] = useState('');
    const [totalShelters, setTotalShelters] = useState(0);
    const [pagination, setPagination] = useState({
        page: 1,
        take: 10,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false
    });

    const handleView = (id) => {
        const shelter = shelters.find(s => s.id === id);
        if (!shelter) {
            toast.error('Shelter data not found.');
            return;
        }

        setViewingShelterId(id);

        // Use already loaded list data to populate the view modal
        setViewShelterData({
            name: shelter.name || '',
            phone: shelter.phone || '',
            email: shelter.email || '',
            address: shelter.address || '',
            cityRegion: shelter.region || '',
            posterLanguage: shelter.posterLanguage || '',
            logo: shelter.logo || null,
            isActive: shelter.isActive !== undefined ? shelter.isActive : true
        });

        setViewLoader(false);
        setIsViewModalOpen(true);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setViewingShelterId(null);
        setViewShelterData(null);
    };

    const handleEdit = (id) => {
        const shelter = shelters.find(s => s.id === id);
        if (shelter) {
            setEditingShelterId(id);
            setFormData({
                name: shelter.name || '',
                phone: shelter.phone || '',
                email: shelter.email || '',
                address: shelter.address || '',
                cityRegion: shelter.region || '',
                posterLanguage: shelter.posterLanguage || '',
                logo: null,
                isActive: shelter.isActive !== undefined ? shelter.isActive : true
            });
            setLogoPreview(shelter.logo || null);
            setFormErrors({});
            setIsEditModalOpen(true);
        }
    };

    const handleDelete = (id) => {
        setDeletingShelterId(id);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setDeletingShelterId(null);
    };

    const confirmDelete = async () => {
        if (!deletingShelterId) return;
        
        setDeleteLoader(true);
        try {
            // Get token from localStorage
            const tokenStr = localStorage.getItem('token');
            let token = '';
            if (tokenStr) {
                try {
                    token = JSON.parse(tokenStr);
                } catch (e) {
                    token = tokenStr;
                }
            }
            
            // Make DELETE API call
            const response = await fetch(`${API_ORIGIN}/api/v1/admin/shelter/delete/${deletingShelterId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            
            if (response.ok && (data.statusCode === 200 || data.statusCode === 201)) {
                toast.success(data.message || 'Shelter deleted successfully!');
                closeDeleteModal();
                // Refresh shelters list
                fetchShelters();
            } else {
                const errorMessage = data.message || data.error || 'Failed to delete shelter. Please try again.';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error deleting shelter:', error);
            toast.error('Failed to delete shelter. Please try again.');
        } finally {
            setDeleteLoader(false);
        }
    };

    // Fetch shelters from API
    const fetchShelters = async () => {
        setLoading(true);
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await getApi(`/api/v1/admin/shelter/all?page=${page}&limit=${limit}${searchParam}`);
            
            if (response && response.statusCode === 200) {
                // Map API response to match component structure
                // Handle different possible response structures
                let sheltersData = [];
                if (Array.isArray(response.data)) {
                    sheltersData = response.data;
                } else if (response.data?.shelters) {
                    sheltersData = response.data.shelters;
                } else if (response.data?.data) {
                    sheltersData = response.data.data;
                } else if (response.shelters) {
                    sheltersData = response.shelters;
                }
                
                const mappedShelters = sheltersData.map((shelter) => {
                    // Handle logo URL - if it's a full URL, use it; otherwise use relative path
                    let logoUrl = '/assets/images/dummy-profile.jpg';
                    if (shelter.logo) {
                        if (shelter.logo.startsWith('http://') || shelter.logo.startsWith('https://')) {
                            logoUrl = shelter.logo;
                        } else if (shelter.logo.startsWith('/')) {
                            logoUrl = shelter.logo;
                        } else {
                            logoUrl = `${API_ORIGIN}${shelter.logo.startsWith('/') ? '' : '/'}${shelter.logo}`;
                        }
                    }
                    
                    return {
                        id: shelter.id || shelter._id,
                        logo: logoUrl,
                        name: shelter.shelter_name || shelter.name || '',
                        phone: shelter.phone_number || shelter.phone || '',
                        email: shelter.email || '',
                        address: shelter.address || '',
                        region: shelter.city_region || shelter.region || '',
                        // language field may come in different keys from API
                        posterLanguage: shelter.poster_language || shelter.posterLanguage || shelter.language || '',
                        isActive: shelter.isActive !== undefined ? shelter.isActive : true
                    };
                });
                
                const total = response.data?.total ?? response.total ?? response.data?.count ?? mappedShelters.length;
                setShelters(mappedShelters);
                setTotalShelters(total);
                // Support API pagination object (like user management); otherwise derive from total & limit
                const pag = response.data?.pagination;
                if (pag) {
                    setPagination({
                        page: pag.page ?? page,
                        take: pag.take ?? pag.limit ?? limit,
                        totalItems: pag.totalItems ?? total,
                        totalPages: pag.totalPages ?? (Math.ceil(total / limit) || 1),
                        hasNextPage: Boolean(pag.hasNextPage),
                        hasPreviousPage: Boolean(pag.hasPreviousPage)
                    });
                } else {
                    const totalPages = Math.ceil(total / limit) || 1;
                    setPagination({
                        page,
                        take: limit,
                        totalItems: total,
                        totalPages,
                        hasNextPage: page < totalPages,
                        hasPreviousPage: page > 1
                    });
                }
            } else {
                // If no data but status is OK, set empty array
                setShelters([]);
                setTotalShelters(0);
                setPagination({ page: 1, take: limit, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false });
            }
        } catch (error) {
            console.error('Error fetching shelters:', error);
            // Don't show error toast if it's handled by getApi
            if (!error.response) {
                toast.error('Failed to fetch shelters. Please try again.');
            }
            setShelters([]);
            setTotalShelters(0);
            setPagination({ page: 1, take: limit, totalItems: 0, totalPages: 0, hasNextPage: false, hasPreviousPage: false });
        } finally {
            setLoading(false);
        }
    };

    // Debounce search input and fetch shelters
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchShelters();
        }, search ? 500 : 0); // 500ms delay for search, immediate for page change

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, search]);

    const handleAddShelter = () => {
        setIsModalOpen(true);
    };

    const handleExport = () => {
        // TODO: Implement export functionality
        toast.info('Export functionality coming soon');
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditModalOpen(false);
        setEditingShelterId(null);
        setFormData({
            name: '',
            phone: '',
            email: '',
            address: '',
            cityRegion: '',
            posterLanguage: '',
            logo: null,
            isActive: true
        });
        setFormErrors({});
        setLogoPreview(null);
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        
        if (name === 'logo' && files && files[0]) {
            const file = files[0];
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.name.trim()) {
            errors.name = 'Shelter name is required';
        }
        
        if (!formData.phone.trim()) {
            errors.phone = 'Phone number is required';
        } else if (!/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ''))) {
            errors.phone = 'Please enter a valid phone number';
        }
        
        if (!formData.email.trim()) {
            errors.email = 'Email ID is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!formData.address.trim()) {
            errors.address = 'Address is required';
        }
        
        if (!formData.cityRegion.trim()) {
            errors.cityRegion = 'City/Region is required';
        }
        
        if (!formData.posterLanguage) {
            errors.posterLanguage = 'Language is required';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // Create FormData for multipart/form-data
            const formDataToSend = new FormData();
            formDataToSend.append('shelter_name', formData.name);
            formDataToSend.append('phone_number', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('city_region', formData.cityRegion);
            formDataToSend.append('poster_language', formData.posterLanguage);
            formDataToSend.append('isActive', formData.isActive ? '1' : '0');
            
            // Add logo file if selected
            if (formData.logo) {
                formDataToSend.append('logo', formData.logo);
            }
            
            // Get token from localStorage
            const tokenStr = localStorage.getItem('token');
            let token = '';
            if (tokenStr) {
                try {
                    token = JSON.parse(tokenStr);
                } catch (e) {
                    token = tokenStr;
                }
            }
            
            // Make API call with multipart/form-data
            const response = await fetch(`${API_ORIGIN}/api/v1/admin/shelter/add`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Don't set Content-Type, let browser set it with boundary for multipart/form-data
                },
                body: formDataToSend
            });
            
            const data = await response.json();
            
            if (response.ok && (data.statusCode === 200 || data.statusCode === 201)) {
                toast.success(data.message || 'Shelter added successfully!');
                closeModal();
                // Refresh shelters list
                fetchShelters();
            } else {
                const errorMessage = data.message || data.error || 'Failed to add shelter. Please try again.';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error adding shelter:', error);
            toast.error('Failed to add shelter. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoader(true);
        try {
            // Create FormData for multipart/form-data
            const formDataToSend = new FormData();
            formDataToSend.append('shelter_name', formData.name);
            formDataToSend.append('phone_number', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('city_region', formData.cityRegion);
            formDataToSend.append('poster_language', formData.posterLanguage);
            formDataToSend.append('isActive', formData.isActive ? '1' : '0');
            
            // Add logo file if selected
            if (formData.logo) {
                formDataToSend.append('logo', formData.logo);
            }
            
            // Get token from localStorage
            const tokenStr = localStorage.getItem('token');
            let token = '';
            if (tokenStr) {
                try {
                    token = JSON.parse(tokenStr);
                } catch (e) {
                    token = tokenStr;
                }
            }
            
            // Make API call with multipart/form-data for update
            const response = await fetch(`${API_ORIGIN}/api/v1/admin/shelter/update/${editingShelterId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Don't set Content-Type, let browser set it with boundary for multipart/form-data
                },
                body: formDataToSend
            });
            
            const data = await response.json();
            
            if (response.ok && (data.statusCode === 200 || data.statusCode === 201)) {
                toast.success(data.message || 'Shelter updated successfully!');
                closeModal();
                // Refresh shelters list
                fetchShelters();
            } else {
                const errorMessage = data.message || data.error || 'Failed to update shelter. Please try again.';
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error updating shelter:', error);
            toast.error('Failed to update shelter. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className='RightSideDiv users-management shelters-management'>
            {/* Statistics Cards */}
            <div className="dashboard-stats">
                <div className="stat-card">
                    <h2 className="stat-number">{totalShelters || shelters.length}</h2>
                    <h3 className="stat-label">Total Shelters</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">43</h2>
                    <h3 className="stat-label">Active found animals</h3>
                </div>
                <div className="stat-card">
                    <h2 className="stat-number">18</h2>
                    <h3 className="stat-label">Posters generated</h3>
                </div>
            </div>

            {/* All Shelters Section */}
            <div className="shelters-header">
                <h2>All Shelters</h2>
                <div className="shelters-header-actions">
                    <button className="add-shelter-btn" onClick={handleAddShelter}>
                        Add Shelter
                    </button>
                    <button className="export-btn" onClick={handleExport}>
                        Export
                    </button>
                </div>
            </div>

            <div className="shelters-section">
                <div className="table-outer">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>Loading shelters...</p>
                        </div>
                    ) : shelters.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p>No shelters found.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr className="t-heading">
                                        <th><h5>Logo</h5></th>
                                        <th><h5>Shelter Name</h5></th>
                                        <th><h5>Phone number</h5></th>
                                        <th><h5>Email</h5></th>
                                        <th><h5>Address</h5></th>
                                        <th><h5>Language</h5></th>
                                        <th><h5>Action</h5></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {shelters.map((shelter) => (
                                <tr key={shelter.id}>
                                    <td>
                                        <div className="shelter-logo">
                                            <img 
                                                src={shelter.logo.startsWith('http') ? shelter.logo : window.getPublicUrl(shelter.logo)} 
                                                alt={shelter.name}
                                                onError={(e) => {
                                                    e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg');
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td><h5>{shelter.name}</h5></td>
                                    <td><h5>{shelter.phone}</h5></td>
                                    <td><h5>{shelter.email}</h5></td>
                                    <td><h5>{shelter.address}</h5></td>
                                    <td><h5>{shelter.posterLanguage}</h5></td>
                                    <td className="action">
                                        <button 
                                            className="action-btn view-btn" 
                                            onClick={() => handleView(shelter.id)}
                                            title="View"
                                        >
                                            <img src={window.getPublicUrl('/assets/images/view.png')} alt="View" />
                                        </button>
                                        <button 
                                            className="action-btn edit-btn" 
                                            onClick={() => handleEdit(shelter.id)}
                                            title="Edit"
                                        >
                                            <img src={window.getPublicUrl('/assets/images/edit.png')} alt="Edit" />
                                        </button>
                                        <button 
                                            className="action-btn delete-btn" 
                                            onClick={() => handleDelete(shelter.id)}
                                            title="Delete"
                                        >
                                            <img src={window.getPublicUrl('/assets/images/del.png')} alt="Delete" />
                                        </button>
                                    </td>
                                </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {/* Pagination */}
                {!loading && pagination.totalPages > 0 && (
                    <div className="pagination-bar">
                        <div className="pagination-controls">
                            <button
                                type="button"
                                className="pagination-btn pagination-prev-next"
                                disabled={!pagination.hasPreviousPage}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                aria-label="Previous page"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <div className="pagination-pages">
                                {(() => {
                                    const { page: currentPage, totalPages } = pagination;
                                    const pages = [];
                                    const showEllipsisStart = totalPages > 5 && currentPage > 3;
                                    const showEllipsisEnd = totalPages > 5 && currentPage < totalPages - 2;
                                    let start = 1;
                                    let end = totalPages;
                                    if (totalPages > 5) {
                                        if (currentPage <= 3) {
                                            start = 1;
                                            end = 4;
                                        } else if (currentPage >= totalPages - 2) {
                                            start = totalPages - 3;
                                            end = totalPages;
                                        } else {
                                            start = currentPage - 1;
                                            end = currentPage + 1;
                                        }
                                    }
                                    if (showEllipsisStart) pages.push(1, 'ellipsis-start');
                                    for (let i = start; i <= end; i++) pages.push(i);
                                    if (showEllipsisEnd && end < totalPages) pages.push('ellipsis-end', totalPages);
                                    return pages.map((item, idx) =>
                                        item === 'ellipsis-start' || item === 'ellipsis-end' ? (
                                            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                                        ) : (
                                            <button
                                                key={item}
                                                type="button"
                                                className={`pagination-btn pagination-page ${item === currentPage ? 'active' : ''}`}
                                                onClick={() => setPage(item)}
                                                aria-label={`Page ${item}`}
                                                aria-current={item === currentPage ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        )
                                    );
                                })()}
                            </div>
                            <button
                                type="button"
                                className="pagination-btn pagination-prev-next"
                                disabled={!pagination.hasNextPage}
                                onClick={() => setPage((p) => p + 1)}
                                aria-label="Next page"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Shelter Modal */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Add Shelter Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Add Shelter</h2>
                        <p>Add Shelter basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-content">
                        {/* Logo Upload Section */}
                        <div className="logo-upload-section">
                            <label htmlFor="logo" className="logo-upload-label">
                                <div className="logo-upload-circle">
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo preview" className="logo-preview-img" />
                                    ) : (
                                        <img src={window.getPublicUrl('/assets/images/Camera.svg')} alt="Upload logo" className="camera-icon" />
                                    )}
                                </div>
                                <span className="upload-logo-text">Upload logo</span>
                            </label>
                            <input
                                type="file"
                                id="logo"
                                name="logo"
                                accept="image/*"
                                onChange={handleInputChange}
                                className="logo-file-input"
                            />
                            {formErrors.logo && <span className="error-message">{formErrors.logo}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="name">Shelter name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter shelter name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="phone">Phone number <span className="required">*</span></label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    className={formErrors.phone ? 'error' : ''}
                                />
                                {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="email">Email ID <span className="required">*</span></label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email ID"
                                    className={formErrors.email ? 'error' : ''}
                                />
                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="address">Address <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter address"
                                    className={formErrors.address ? 'error' : ''}
                                />
                                {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="cityRegion">City/Region <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="cityRegion"
                                    name="cityRegion"
                                    value={formData.cityRegion}
                                    onChange={handleInputChange}
                                    placeholder="Enter city or region"
                                    className={formErrors.cityRegion ? 'error' : ''}
                                />
                                {formErrors.cityRegion && <span className="error-message">{formErrors.cityRegion}</span>}
                            </div>
                        </div>

                        <CustomDropdown
                            label="Poster language"
                            required={true}
                            name="posterLanguage"
                            value={formData.posterLanguage}
                            onChange={handleInputChange}
                            placeholder="Select language"
                            options={[
                                { label: 'English', value: 'English' },
                                { label: 'French', value: 'French' },
                            ]}
                            error={formErrors.posterLanguage}
                        />
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Adding...' : 'Add Shelter'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Shelter Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={closeModal}
                contentLabel="Edit Shelter Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>Edit Shelter</h2>
                        <p>Edit Shelter basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleUpdateSubmit}>
                    <div className="form-content">
                        {/* Logo Upload Section (same as Add) */}
                        <div className="logo-upload-section">
                            <label htmlFor="edit-logo" className="logo-upload-label">
                                <div className="logo-upload-circle">
                                    {logoPreview ? (
                                        <img src={logoPreview} alt="Logo preview" className="logo-preview-img" />
                                    ) : (
                                        <img src={window.getPublicUrl('/assets/images/Camera.svg')} alt="Upload logo" className="camera-icon" />
                                    )}
                                </div>
                                <span className="upload-logo-text">Upload logo</span>
                            </label>
                            <input
                                type="file"
                                id="edit-logo"
                                name="logo"
                                accept="image/*"
                                onChange={handleInputChange}
                                className="logo-file-input"
                            />
                            {formErrors.logo && <span className="error-message">{formErrors.logo}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-name">Shelter name <span className="required">*</span></label>
                            <input
                                type="text"
                                id="edit-name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter shelter name"
                                className={formErrors.name ? 'error' : ''}
                            />
                            {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-phone">Phone number <span className="required">*</span></label>
                                <input
                                    type="tel"
                                    id="edit-phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    className={formErrors.phone ? 'error' : ''}
                                />
                                {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-email">Email ID <span className="required">*</span></label>
                                <input
                                    type="email"
                                    id="edit-email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email ID"
                                    className={formErrors.email ? 'error' : ''}
                                />
                                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label htmlFor="edit-address">Address <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="edit-address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter address"
                                    className={formErrors.address ? 'error' : ''}
                                />
                                {formErrors.address && <span className="error-message">{formErrors.address}</span>}
                            </div>

                            <div className="form-group form-group-half">
                                <label htmlFor="edit-cityRegion">City/Region <span className="required">*</span></label>
                                <input
                                    type="text"
                                    id="edit-cityRegion"
                                    name="cityRegion"
                                    value={formData.cityRegion}
                                    onChange={handleInputChange}
                                    placeholder="Enter city or region"
                                    className={formErrors.cityRegion ? 'error' : ''}
                                />
                                {formErrors.cityRegion && <span className="error-message">{formErrors.cityRegion}</span>}
                            </div>
                        </div>

                        <CustomDropdown
                            label="Poster language"
                            required={true}
                            name="posterLanguage"
                            value={formData.posterLanguage}
                            onChange={handleInputChange}
                            placeholder="Select language"
                            options={[
                                { label: 'English', value: 'English' },
                                { label: 'French', value: 'French' },
                            ]}
                            error={formErrors.posterLanguage}
                        />
                    </div>

                    <div className="modal-footer">
                        <button type="submit" className="submit-btn" disabled={loader}>
                            {loader ? 'Updating...' : 'Update Shelter'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onRequestClose={closeDeleteModal}
                contentLabel="Delete Confirmation Modal"
                className="delete-confirmation-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="delete-modal-header">
                    <button className="close-btn" onClick={closeDeleteModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="delete-modal-content">
                    <div className="delete-warning-icon">
                        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="32" cy="32" r="32" fill="#f44336"/>
                            <path d="M32 20V36" stroke="white" strokeWidth="4" strokeLinecap="round"/>
                            <circle cx="32" cy="44" r="2" fill="white"/>
                        </svg>
                    </div>
                    
                    <h2 className="delete-modal-title">Delete Confirmation</h2>
                    
                    <p className="delete-modal-message">
                        Are you sure you want to delete this shelter
                    </p>
                    
                    <div className="delete-modal-actions">
                        <button 
                            className="delete-confirm-btn" 
                            onClick={confirmDelete}
                            disabled={deleteLoader}
                        >
                            {deleteLoader ? 'Deleting...' : 'Delete'}
                        </button>
                        <button 
                            className="delete-cancel-btn" 
                            onClick={closeDeleteModal}
                            disabled={deleteLoader}
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </Modal>

            {/* View Shelter Modal */}
            <Modal
                isOpen={isViewModalOpen}
                onRequestClose={closeViewModal}
                contentLabel="View Shelter Modal"
                className="add-shelter-modal"
                overlayClassName="add-shelter-overlay"
                ariaHideApp={false}
            >
                <div className="modal-header">
                    <div>
                        <h2>View Shelter</h2>
                        <p>View Shelter basic information</p>
                    </div>
                    <button className="close-btn" onClick={closeViewModal}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                {viewLoader ? (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>Loading shelter details...</p>
                    </div>
                ) : viewShelterData ? (
                    <form>
                        <div className="form-content">
                            {/* Logo Display Section */}
                            <div className="logo-upload-section">
                                <div className="logo-upload-label">
                                    <div className="logo-upload-circle">
                                        {viewShelterData.logo ? (
                                            <img 
                                                src={viewShelterData.logo.startsWith('http') ? viewShelterData.logo : window.getPublicUrl(viewShelterData.logo)} 
                                                alt="Shelter logo" 
                                                className="logo-preview-img"
                                                onError={(e) => {
                                                    e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg');
                                                }}
                                            />
                                        ) : (
                                            <img src={window.getPublicUrl('/assets/images/Camera.svg')} alt="No logo" className="camera-icon" />
                                        )}
                                    </div>
                                    <span className="upload-logo-text">Logo</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="view-name">Shelter name</label>
                                <input
                                    type="text"
                                    id="view-name"
                                    value={viewShelterData.name || ''}
                                    disabled
                                    readOnly
                                    className="view-input-disabled"
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group form-group-half">
                                    <label htmlFor="view-phone">Phone number</label>
                                    <input
                                        type="tel"
                                        id="view-phone"
                                        value={viewShelterData.phone || ''}
                                        disabled
                                        readOnly
                                        className="view-input-disabled"
                                    />
                                </div>

                                <div className="form-group form-group-half">
                                    <label htmlFor="view-email">Email ID</label>
                                    <input
                                        type="email"
                                        id="view-email"
                                        value={viewShelterData.email || ''}
                                        disabled
                                        readOnly
                                        className="view-input-disabled"
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group form-group-half">
                                    <label htmlFor="view-address">Address</label>
                                    <input
                                        type="text"
                                        id="view-address"
                                        value={viewShelterData.address || ''}
                                        disabled
                                        readOnly
                                        className="view-input-disabled"
                                    />
                                </div>

                                <div className="form-group form-group-half">
                                    <label htmlFor="view-cityRegion">City/Region</label>
                                    <input
                                        type="text"
                                        id="view-cityRegion"
                                        value={viewShelterData.cityRegion || ''}
                                        disabled
                                        readOnly
                                        className="view-input-disabled"
                                    />
                                </div>
                            </div>

                            <CustomDropdown
                                label="Poster language"
                                name="view-posterLanguage"
                                value={viewShelterData.posterLanguage || ''}
                                placeholder="Select language"
                                options={[
                                    { label: 'English', value: 'English' },
                                    { label: 'French', value: 'French' },
                                ]}
                                disabled={true}
                            />
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="submit-btn" onClick={closeViewModal}>
                                Close
                            </button>
                        </div>
                    </form>
                ) : (
                    <div style={{ padding: '30px', textAlign: 'center' }}>
                        <p>No data available</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Dashboard;
